import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { TableColumnsProps } from "@lib/types/General";
import { CustomerVoipConfig } from "@lib/types/Voip";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { Tooltip } from "antd";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { AppTag } from "@lib/components/AppComponents";
import IntlMessages from "@lib/helpers/IntlMessages";

type Props<T> = TableColumnsProps<T> & {
  handleSelect: (item: CustomerVoipConfig) => void;
  handleDelete: (item: CustomerVoipConfig) => void;
};

const getColumns = <T extends CustomerVoipConfig>(
  props: Props<T>,
): ColumnsType<T> => {
  const { handleSelect, handleDelete, messages, detailContext } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (v, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
      width: "180px",
      filterType: "search",
    },

    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "150px",
      render: (_, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },

    {
      title: "common.name",
      dataIndex: "name",
      key: "name",
      width: "200px",
      filterType: "search",
    },

    {
      title: "common.phone",
      dataIndex: "phone",
      key: "phone",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.terminationURI",
      dataIndex: "terminationURI",
      key: "terminationURI",
      width: "200px",
      filterType: "search",
      render: (d) =>
        d ? <Tooltip title={d}>{shortenString(d, 30)}</Tooltip> : "-",
    },

    {
      title: "common.username",
      dataIndex: "username",
      key: "username",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.nickname",
      dataIndex: "nickname",
      key: "nickname",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.isDisabled",
      dataIndex: "isDisabled",
      key: "isDisabled",
      width: "120px",
      filterType: "dropdown",
      filters: [
        { text: messages["common.enabled"] as string, value: true },
        { text: messages["common.disabled"] as string, value: false },
      ],
      render: (d) => (
        <AppTag color={d ? "red" : "green"}>
          <IntlMessages id={d ? "common.disabled" : "common.enabled"} />
        </AppTag>
      ),
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "150px",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-"),
    },

    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "150px",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-"),
    },

    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "150px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelect(record)}
            noLabel
          />
          <ActionButton
            type="delete"
            onClick={() => handleDelete(record)}
            itemName="sidebar.customerVoipConfigs"
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
